import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import Footer from '../components/Footer';
import HomeBanner from '../components/HomeBanner';
import api from '../services/api';
import { 
  TruckIcon,
  RocketLaunchIcon,
  BoltIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  Cog6ToothIcon,
  KeyIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ShieldCheckIcon,
  BellIcon
} from '@heroicons/react/24/outline';

const HomePage = () => {
  const { isAuthenticated, user, logout } = useOutletContext() || {};
  const isAdmin = ['admin'].includes(String(user?.ruolo || user?.role || '').toLowerCase());
  const [loading, setLoading] = useState(true);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [notifiche, setNotifiche] = useState([]);
  const [notificheBici, setNotificheBici] = useState([]);

  useEffect(() => {
    // Verifica lo stato di autenticazione
    setLoading(false);
  }, []);

  // Carica le notifiche se l'utente è loggato
  useEffect(() => {
    const fetchNotifiche = async () => {
      try {
        const response = await api.get('/notifiche');
        if (response.data && response.data.success) {
          const data = response.data.data;
          // Combina scadenze offerte e pratiche, poi separa per modulo:
          // le notifiche bici vanno sul widget Bike, le altre sul widget NLT.
          const combinate = [
            ...(data.scadenzeOfferte || []),
            ...(data.pratiche || [])
          ];
          setNotifiche(combinate.filter(n => n.datiAggiuntivi?.modulo !== 'bici'));
          setNotificheBici(combinate.filter(n => n.datiAggiuntivi?.modulo === 'bici'));
        }
      } catch (error) {
        console.error('Errore nel caricamento notifiche:', error);
      }
    };

    if (user && isAuthenticated) {
      // Carica immediatamente
      fetchNotifiche();
      
      // Ricarica le notifiche ogni 10 secondi (più frequente per aggiornamenti real-time)
      const interval = setInterval(fetchNotifiche, 10000);
      
      // Ricarica quando la finestra torna in focus
      const handleFocus = () => {
        fetchNotifiche();
      };
      window.addEventListener('focus', handleFocus);
      
      // Ricarica quando la visibilità della pagina cambia (tab attiva)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          fetchNotifiche();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user, isAuthenticated]);

  // Funzione per ottenere le iniziali dell'utente per l'avatar
  const getUserInitials = () => {
    if (user && user.nome && user.cognome) {
      return `${user.nome.charAt(0)}${user.cognome.charAt(0)}`.toUpperCase();
    }
    return user?.username?.charAt(0).toUpperCase() || 'U';
  };

  const handleLogout = () => {
    logout();
  };

  // Array delle cards da mostrare (solo se loggato)
  const cards = [
    {
      id: 'long-term-rental',
      title: 'Noleggio a Lungo Termine',
      description: 'Gestione noleggi a lungo termine',
      icon: <TruckIcon className="h-16 w-16 text-blue-500" />,
      link: '/nlt',
      isDisabled: false
    },
    {
      id: 'bike-mobility',
      title: 'Bike Mobility',
      description: 'Soluzioni di mobilità con biciclette',
      icon: <BoltIcon className="h-16 w-16 text-emerald-500" />,
      link: '/bike',
      isDisabled: false
    },
    {
      id: 'short-term-rental',
      title: 'Noleggio a Breve Termine',
      description: 'Accedi alla piattaforma di noleggio a breve termine',
      icon: <CalendarDaysIcon className="h-16 w-16 text-blue-500" />,
      link: 'https://one.avisworld.com/it_IT/Avis-WAPOO/',
      isExternal: true
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Se l'utente è loggato, mostra le cards dei servizi
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-slate-50 flex flex-col">
        {/* Header della dashboard - Stile Apple */}
        <header className="bg-white/80 backdrop-blur-xl border-b border-gray-100/50 sticky top-0 z-50 shadow-lg shadow-gray-200/20">
          <div className="max-w-[1800px] mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16 sm:h-18">
              {/* Logo */}
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Link to="/" className="flex items-center group">
                    <div className="relative">
                      <img 
                        src="/logo.png" 
                        alt="Logo WAPOO" 
                        className="h-10 sm:h-12 w-auto transition-all duration-300 group-hover:scale-105" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-lg"></div>
                    </div>
                  </Link>
                </div>
              </div>
              
              {/* Avatar utente */}
              <div className="flex items-center">
                <div className="hidden sm:block text-right mr-4">
                  <div className="text-sm font-semibold text-gray-900 tracking-tight">{user?.nome} {user?.cognome}</div>
                  <div className="text-xs text-gray-500 font-medium">{user?.email}</div>
                </div>
                <div className="relative">
                  <button
                    className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 hover:bg-white/50 p-1.5"
                    id="user-menu"
                    aria-haspopup="true"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  >
                    <span className="sr-only">Menu utente</span>
                    <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-2xl flex items-center justify-center bg-gradient-to-br from-red-500 to-pink-600 text-white shadow-lg shadow-red-500/25 font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-red-500/30">
                      {getUserInitials()}
                    </div>
                  </button>
                  
                  {/* Menu utente - Stile Apple */}
                  {isUserMenuOpen && (
                    <div
                      className="origin-top-right absolute right-0 mt-3 w-72 rounded-2xl shadow-2xl bg-white/90 backdrop-blur-xl ring-1 ring-gray-200/50 z-50 border border-white/20"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="user-menu"
                    >
                      <div className="py-3">
                        {/* Header del menu - mobile */}
                        <div className="px-6 py-4 border-b border-gray-100/80 sm:hidden">
                          <div className="flex items-center">
                            <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-red-500 to-pink-600 text-white font-semibold shadow-lg">
                              {getUserInitials()}
                            </div>
                            <div className="ml-4">
                              <div className="font-semibold text-gray-900 text-base tracking-tight">{user?.nome} {user?.cognome}</div>
                              <div className="text-sm text-gray-500 font-medium">{user?.email}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Voci del menu - Stile Apple */}
                        <div className="px-2 py-2">
                          <Link
                            to="/profilo"
                            className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 rounded-xl transition-all duration-200 group"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <UserCircleIcon className="mr-3 h-5 w-5 text-gray-500 group-hover:text-blue-500 transition-colors duration-200" />
                            <span className="font-medium">Profilo</span>
                          </Link>
                          
                          <Link
                            to="/nlt/impostazioni"
                            className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 rounded-xl transition-all duration-200 group"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Cog6ToothIcon className="mr-3 h-5 w-5 text-gray-500 group-hover:text-purple-500 transition-colors duration-200" />
                            <span className="font-medium">Impostazioni</span>
                          </Link>
                          
                          <Link
                            to="/cambia-password"
                            className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 rounded-xl transition-all duration-200 group"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <KeyIcon className="mr-3 h-5 w-5 text-gray-500 group-hover:text-emerald-500 transition-colors duration-200" />
                            <span className="font-medium">Cambia password</span>
                          </Link>
                        </div>
                        
                        {/* Rete Multilivello */}
                        <div className="border-t border-gray-100/80 mx-2 mt-2">
                          <div className="px-4 py-2">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">RETE MULTILIVELLO</div>
                          </div>
                          <div className="px-2 py-2">
                            <Link
                              to="/rete-multilivello"
                              className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 rounded-xl transition-all duration-200 group"
                              role="menuitem"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <BuildingOfficeIcon className="mr-3 h-5 w-5 text-gray-500 group-hover:text-blue-500 transition-colors duration-200" />
                              <span className="font-medium">Dashboard Rete</span>
                            </Link>
                            
                            <Link
                              to="/gestione-utenti"
                              className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 rounded-xl transition-all duration-200 group"
                              role="menuitem"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <UsersIcon className="mr-3 h-5 w-5 text-gray-500 group-hover:text-green-500 transition-colors duration-200" />
                              <span className="font-medium">Gestione Utenti</span>
                            </Link>
                            
                            <Link
                              to="/gestione-ruoli"
                              className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 rounded-xl transition-all duration-200 group"
                              role="menuitem"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <ShieldCheckIcon className="mr-3 h-5 w-5 text-gray-500 group-hover:text-purple-500 transition-colors duration-200" />
                              <span className="font-medium">Gestione Ruoli</span>
                            </Link>
                          </div>
                        </div>
                        
                        <div className="border-t border-gray-100/80 mx-2 mt-2">
                          <div className="px-2 py-2">
                            <button
                              className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50/80 rounded-xl transition-all duration-200 group"
                              role="menuitem"
                              onClick={() => {
                                setIsUserMenuOpen(false);
                                handleLogout();
                              }}
                            >
                              <ArrowLeftOnRectangleIcon className="mr-3 h-5 w-5 text-red-500 group-hover:text-red-600 transition-colors duration-200" />
                              <span className="font-medium">Logout</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Contenuto principale con le cards */}
        <main className="flex-1">
          <div className="max-w-[1800px] mx-auto mt-0">
            <HomeBanner />
          </div>
          <div className="py-8">
            <div className="max-w-[1800px] mx-auto">
              <h2 className="text-2xl font-bold text-blue-900 px-4 md:px-6 mb-4">Gestisci</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 px-4 md:px-6">
                {cards.map(card => (
                  card.isExternal ? (
                    <a 
                      key={card.id} 
                      href={card.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="block"
                    >
                      <div className="bg-white rounded-md shadow h-full flex flex-col items-center justify-center text-center p-6 transition-transform hover:scale-105">
                        <div className="mb-4">
                          {card.icon}
                        </div>
                        <h3 className="text-xl font-bold text-blue-900 mb-2">{card.title}</h3>
                        <p className="text-sm text-gray-600">{card.description}</p>
                      </div>
                    </a>
                  ) : (
                    card.isDisabled ? (
                      <div key={card.id} className="block opacity-50 cursor-not-allowed">
                        <div className="bg-white rounded-md shadow h-full flex flex-col items-center justify-center text-center p-6">
                          <div className="mb-4">
                            {card.icon}
                          </div>
                          <h3 className="text-xl font-bold text-blue-900 mb-2">{card.title}</h3>
                          <p className="text-sm text-gray-600">{card.description}</p>
                          <span className="mt-2 text-xs text-gray-500 italic">Prossimamente disponibile</span>
                        </div>
                      </div>
                    ) : (
                      <Link key={card.id} to={card.link} className="block">
                        <div className="bg-white rounded-md shadow h-full flex flex-col items-center justify-center text-center p-6 transition-transform hover:scale-105 relative">
                          {/* Badge notifiche per NLT */}
                          {card.id === 'long-term-rental' && notifiche.filter(n => !n.letta).length > 0 && (
                            <div className="absolute top-4 right-4">
                              <div className="relative">
                                <BellIcon className="h-6 w-6 text-blue-500" />
                                <span className="absolute -top-1 -right-1 flex h-5 w-5">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 items-center justify-center text-white text-[10px] font-bold">
                                    {notifiche.filter(n => !n.letta).length > 9 ? '9+' : notifiche.filter(n => !n.letta).length}
                                  </span>
                                </span>
                              </div>
                            </div>
                          )}
                          {/* Badge notifiche per Bike */}
                          {card.id === 'bike-mobility' && notificheBici.filter(n => !n.letta).length > 0 && (
                            <div className="absolute top-4 right-4">
                              <div className="relative">
                                <BellIcon className="h-6 w-6 text-emerald-500" />
                                <span className="absolute -top-1 -right-1 flex h-5 w-5">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 items-center justify-center text-white text-[10px] font-bold">
                                    {notificheBici.filter(n => !n.letta).length > 9 ? '9+' : notificheBici.filter(n => !n.letta).length}
                                  </span>
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="mb-4">
                            {card.icon}
                          </div>
                          <h3 className="text-xl font-bold text-blue-900 mb-2">{card.title}</h3>
                          <p className="text-sm text-gray-600">{card.description}</p>
                          
                          {/* Indicatore notifiche testuale per NLT */}
                          {card.id === 'long-term-rental' && notifiche.filter(n => !n.letta).length > 0 && (
                            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded-full text-xs font-medium">
                              <BellIcon className="h-4 w-4" />
                              <span>{notifiche.filter(n => !n.letta).length} {notifiche.filter(n => !n.letta).length === 1 ? 'nuova notifica' : 'nuove notifiche'}</span>
                            </div>
                          )}
                          {/* Indicatore notifiche testuale per Bike */}
                          {card.id === 'bike-mobility' && notificheBici.filter(n => !n.letta).length > 0 && (
                            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                              <BellIcon className="h-4 w-4" />
                              <span>{notificheBici.filter(n => !n.letta).length} {notificheBici.filter(n => !n.letta).length === 1 ? 'nuova notifica' : 'nuove notifiche'}</span>
                            </div>
                          )}
                        </div>
                      </Link>
                    )
                  )
                ))}
              </div>
            </div>
          </div>
        </main>

        <Footer variant="minimal" />
      </div>
    );
  }

  // Se NON è loggato, mostra la landing page pubblica
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-slate-50">
      {/* Header pubblico - Stile Apple */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 h-16">
            <div className="flex items-center">
              <div className="relative group">
                <img 
                  src="/logo.png" 
                  alt="Logo WAPOO" 
                  className="h-10 w-auto transition-all duration-300 group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-lg"></div>
              </div>
            </div>
            <div className="flex items-center">
              <Link
                to="/login"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-blue-600/25 hover:shadow-xl hover:scale-105 hover:shadow-blue-600/30"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Full Screen */}
      <main className="flex-1">
        <div className="min-h-[calc(100vh-80px)] flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Contenuto a sinistra */}
              <div className="space-y-8">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Amplia il tuo business con
                  <br />
                  <span className="text-blue-600">i servizi di Wapoo.</span>
                </h1>
                
                <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
                  Noleggio a Lungo Termine, Breve Termine e Bike Mobility. 
                  Tutto in un'unica piattaforma per espandere la tua attività di mobilità.
                </p>
                
                <div className="pt-4">
                  <Link
                    to="/login"
                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-md text-lg font-medium transition-colors shadow-lg hover:shadow-xl"
                  >
                    Unisciti a noi
                  </Link>
                </div>
              </div>

              {/* Immagine a destra */}
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-xl">
                    <img 
                      src="/dashboard-photo.jpg" 
                      alt="Dashboard WAPOO" 
                      className="w-full max-w-lg h-auto rounded-lg shadow-lg"
                    />
                  </div>
                  {/* Elementi decorativi */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-200 rounded-full opacity-20"></div>
                  <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-300 rounded-full opacity-15"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage; 